import type { OnboardingTemplate } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

// SPEC-FORM-001 (Batch 2): a criação só pede Nome. Os campos restantes
// herdam defaults — sidebarBlockLabel = nome em UPPERCASE, ownerId =
// manager corrente, state = draft. O modal também serve modo edit.

type Mode = "create" | "edit" | "duplicate";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  template?: OnboardingTemplate | null;
  onCreated?: (template: OnboardingTemplate) => void;
  onUpdated?: (template: OnboardingTemplate) => void;
};

type RpcError = {
  data?: { code?: string };
  message?: string;
};

export function CreateTemplateModal({
  open,
  onOpenChange,
  mode,
  template,
  onCreated,
  onUpdated,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [errors, setErrors] = useState<{ name?: string }>({});

  const isEdit = mode === "edit" && !!template;
  const isDuplicate = mode === "duplicate" && !!template;

  useEffect(() => {
    if (!open) return;
    if (isEdit && template) {
      setName(template.name);
    } else if (isDuplicate && template) {
      setName(`${template.name} (cópia)`);
    } else {
      setName("");
    }
    setErrors({});
  }, [open, isEdit, isDuplicate, template]);

  const createMut = useMutation({
    mutationFn: (input: { name: string }) =>
      rpcClient.onboardingTemplates.create({
        name: input.name,
        sidebarBlockLabel: input.name.toUpperCase(),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      toast({ title: "Template criado" });
      onOpenChange(false);
      onCreated?.(created as OnboardingTemplate);
    },
    onError: (err: unknown) => {
      handleRpcError(err);
    },
  });

  const duplicateMut = useMutation({
    mutationFn: (input: { templateId: string; name: string }) =>
      rpcClient.onboardingTemplates.duplicate({
        templateId: input.templateId,
        name: input.name,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      toast({ title: "Template duplicado" });
      onOpenChange(false);
      onCreated?.(created as OnboardingTemplate);
    },
    onError: (err: unknown) => {
      handleRpcError(err);
    },
  });

  const updateMut = useMutation({
    mutationFn: (input: {
      id: string;
      name: string;
      sidebarBlockLabel: string;
      ownerId: string;
      welcomeMessages: Record<string, string>;
    }) => rpcClient.onboardingTemplates.update(input),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      if (template) {
        await queryClient.invalidateQueries({
          queryKey: orpc.onboardingTemplates.getById.queryOptions({ input: { id: template.id } })
            .queryKey,
        });
      }
      toast({ title: "Template atualizado" });
      onOpenChange(false);
      onUpdated?.(updated as OnboardingTemplate);
    },
    onError: (err: unknown) => {
      handleRpcError(err);
    },
  });

  function handleRpcError(err: unknown) {
    const rpcErr = err as RpcError;
    const code = rpcErr?.data?.code;
    if (code === "duplicate_name") {
      setErrors({
        name: rpcErr.message ?? "Já existe um template com esse nome.",
      });
      return;
    }
    if (code === "name_required") {
      setErrors({ name: "Informe o nome do template" });
      return;
    }
    toast({
      title: isEdit
        ? "Não foi possível atualizar o template"
        : isDuplicate
          ? "Não foi possível duplicar o template"
          : "Não foi possível criar o template",
      description: rpcErr?.message ?? String(err),
      variant: "destructive",
    });
  }

  function handleSubmit() {
    const trimmed = name.trim();
    const nextErrors: typeof errors = {};
    if (!trimmed) nextErrors.name = "Informe o nome do template";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (isDuplicate && template) {
      duplicateMut.mutate({ templateId: template.id, name: trimmed });
      return;
    }
    if (isEdit && template) {
      updateMut.mutate({
        id: template.id,
        name: trimmed,
        sidebarBlockLabel: template.sidebarBlockLabel,
        ownerId: template.ownerId,
        welcomeMessages: template.welcomeMessages ?? {},
      });
      return;
    }
    createMut.mutate({ name: trimmed });
  }

  const pending = createMut.isPending || updateMut.isPending || duplicateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-create-template" className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Editar template" : isDuplicate ? "Duplicar template" : "Novo template"}
          </DialogTitle>
          {isEdit && <DialogDescription>Atualize o nome do template.</DialogDescription>}
          {isDuplicate && (
            <DialogDescription>
              Crie uma cópia do template "{template?.name}" com um novo nome.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="create-tpl-name">Nome do template</Label>
            <Input
              id="create-tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              data-testid="input-create-template-name"
            />
            {errors.name && <p className="mt-1 text-sm text-destructive">{errors.name}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={pending}
            data-testid="button-cancel-create-template"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={pending}
            data-testid="button-submit-create-template"
          >
            {pending
              ? "Salvando…"
              : isEdit
                ? "Salvar"
                : isDuplicate
                  ? "Duplicar"
                  : "Criar template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
